import { Injectable } from '@angular/core';
import { KalDialogConfig, KalDialogService } from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';
import gql from 'graphql-tag';
import cloneDeep from 'lodash-es/cloneDeep';
import get from 'lodash-es/get';
import { Observable, throwError } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { ReportPrintService } from 'src/app/_core/services/report-print.service';
import { MailParameters } from 'src/app/_shared/components/mail/shared-mail-container/shared-mail-container.component';
import { GeneratedMailDocument } from 'src/app/_shared/components/mail/shared-mail/shared-mail.component';
import { ViewerDocument } from 'src/app/_shared/components/viewer-document/viewer-document.component';
import {
    SharedChequeLetterDialogComponent,
    SharedChequeLetterDialogData
} from 'src/app/_shared/dialogs/shared-cheque-letter-dialog/shared-cheque-letter-dialog.component';
import {
    ViewerDocumentDialogComponent,
    ViewerDocumentDialogData
} from 'src/app/_shared/dialogs/viewer-document-dialog/viewer-document-dialog.component';
import { UserUpdateApplicationParametersAction } from 'src/app/_user/store/user/user.action';
import { UserState } from 'src/app/_user/store/user/user.state';
import {
    ActivityPurchasesNewOrderSelectPrintsDialogData
} from 'src/app/board/activity/activity-shared/activity-suppliers-orders/dialogs/new-order/activity-purchases-new-order-select-prints-dialog/activity-purchases-new-order-select-prints-dialog.component';
import {
    ActivityPurchasesNewOrderState
} from 'src/app/board/activity/activity-shared/activity-suppliers-orders/stores/activity-purchases-new-order/activity-purchases-new-order.state';
import { RouteActivityType } from 'src/app/board/activity/activity.constants';
import {
    ActivityPrintMailInformations,
    ActivitySalesState
} from 'src/app/board/activity/stores/activity-sales/activity-sales.state';
import { arrayNotEmpty } from 'src/app/utils/array.utils';
import { applicationCodes } from 'src/environments/application-codes';
import { CodesReports } from 'src/environments/codes-reports';
import {
    FichierCompletModel,
    MutationGetIdCeCommandeArgs,
    MutationGetListeCourrierGeneriqueArgs,
    MutationHaveReportsLettreChequeArgs,
    MutationInitialisationModeleImpressionArgs,
    MutationSaveModeleImpressionArgs,
    ParametreImpressionDto,
    ParametresUserAppliModelDto
} from 'src/graphql/generated-types';

export class ActivityPrintMailSelection {

    invoice?: boolean;

    supplierInvoice?: boolean;

    deliveryVouchers?: boolean;

    saleReceipts?: boolean;

    orders?: boolean;

    receipt?: boolean;

    forms?: boolean;

    chequeLetter?: boolean;

    budgetCommitment?: boolean;

    isContextBenefit?: boolean;

    isWithoutSelection?: boolean;
}


export interface ActivitySaleReportSavedParameters {

    invoice: ParametresUserAppliModelDto;

    deliveryVouchers?: ParametresUserAppliModelDto;

    saleReceipts?: ParametresUserAppliModelDto;

    order: ParametresUserAppliModelDto;

}

@Injectable({
    providedIn: 'root'
})
export class ActivityPrintMailService {

    constructor(private readonly kalDialogService: KalDialogService,
        private readonly graphqlService: GraphqlService,
        private readonly store: Store,
        private readonly reportPrintService: ReportPrintService) {
    }

    get shouldOpenPopupByParameters(): boolean {
        const mailParams: ActivitySaleReportSavedParameters = {
            invoice: this.getApplicationParameterFromCode(applicationCodes.IMPFAC),
            order: this.getApplicationParameterFromCode(applicationCodes.IMPCOB)
        };
        const printParams: ActivitySaleReportSavedParameters = {
            invoice: this.getApplicationParameterFromCode(applicationCodes.IMPFAC),
            deliveryVouchers: this.getApplicationParameterFromCode(applicationCodes.IMPBL),
            saleReceipts: this.getApplicationParameterFromCode(applicationCodes.IMRCV),
            order: this.getApplicationParameterFromCode(applicationCodes.IMPCOB)
        };

        // Si les informations ont étés sauvegardé la valeur du paramètre est à 0
        const hasSavedInfos = get(this.getApplicationParameterFromCode(applicationCodes.IMPPVN), 'CodeValeur', '1') === '0';

        // Si l'un des paramètre est null ou que les informations n'ont pas été sauvegardé il faut ouvrir la popup de choix des options
        return [
            ...Object.keys(mailParams).map(k => mailParams[k]),
            ...Object.keys(printParams).map(k => printParams[k])
        ].some(p => !p) || !hasSavedInfos;
    }

    get shouldOpenPurchasesPrintDialog(): boolean {
        return this.applicationParameters.some(({ CodeChamp, CodeValeur }) => CodeChamp === applicationCodes.IMPPIC && CodeValeur === '1');
    }

    private get applicationParameters(): ParametresUserAppliModelDto[] {
        return this.store.selectSnapshot(UserState.applicationParameters);
    }

    private get getApplicationParametersFilteredActivityId(): ParametresUserAppliModelDto[] {
        const activityId = this.store.selectSnapshot(ActivitySalesState.activityId);
        return this.applicationParameters.filter(p => p.IdActivite === activityId);
    }

    private get defaultApplicationParameters(): ParametresUserAppliModelDto[] {
        return this.applicationParameters.filter(p => p.IdActivite === null);
    }

    private get idCE(): number {
        return this.store.selectSnapshot(UserState.currentDelegation).Id || null;
    }

    openPrintCheckLetterDialog$(dialogConfigData: SharedChequeLetterDialogData): Observable<SharedChequeLetterDialogData> {
        const baseConfig: KalDialogConfig = {
            title: 'Impression de la lettre chèque',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop'
        };

        const config = new KalDialogConfig<SharedChequeLetterDialogData>({
            ...baseConfig,
            data: dialogConfigData
        });

        return this.kalDialogService.open(SharedChequeLetterDialogComponent, config).afterClosed.pipe(
            filter(dialogResult => !!dialogResult?.accepted),
            tap(dialogResult => {
                const config = new KalDialogConfig<ViewerDocumentDialogData>({
                    ...baseConfig,
                    disableClose: true,
                    data: {
                        viewerDocumentsList: [dialogResult.document]
                    }
                });

                this.kalDialogService.open(ViewerDocumentDialogComponent, config);
            })
        );
    }

    /**
     * Récupère les valeurs à sélectionner dans la dialogue des impressions et mails en fontion des paramètres d'application
     */
    getPrintMailSelectedValuesFromApplicationParameters(): { printOptions, mailOptions } {
        const printOptions = {
            invoice: get(this.getApplicationParameterFromCode(applicationCodes.IMPFAC), 'CodeValeur', '0') === '1',
            saleReceipts: get(this.getApplicationParameterFromCode(applicationCodes.IMRCV), 'CodeValeur', '0') === '1',
            deliveryVouchers: get(this.getApplicationParameterFromCode(applicationCodes.IMPBL), 'CodeValeur', '0') === '1',
            orders: get(this.getApplicationParameterFromCode(applicationCodes.IMPCOB), 'CodeValeur', '0') === '1',
            forms: get(this.getApplicationParameterFromCode(applicationCodes.MAIL_FORMULAIRE), 'CodeValeur','0') === '1'
        };
        const mailOptions = {
            invoice: get(this.getApplicationParameterFromCode(applicationCodes.MAILFA), 'CodeValeur', '0') === '1',
            orders: get(this.getApplicationParameterFromCode(applicationCodes.MAILCB), 'CodeValeur', '0') === '1',
            forms: get(this.getApplicationParameterFromCode(applicationCodes.MAIL_FORMULAIRE), 'CodeValeur', '0') === '1'
        };
        return { printOptions, mailOptions };
    }

    getBeneficiaryViewerDocumentsListByPrintInfos(printInformations: ActivityPrintMailInformations, printSelection: ActivityPrintMailSelection): ViewerDocument[] {
        const {
            invoiceId,
            invoiceNumber,
            deliveryVouchersId,
            ordersId,
            activityType
        } = printInformations;
        const invoiceLabel = printInformations.invoiceLabel || 'Facture';
        const { invoice, saleReceipts, orders, deliveryVouchers, receipt, forms } = printSelection;
        const viewerDocumentsList: ViewerDocument[] = [];
        const orderId = arrayNotEmpty(ordersId) ? ordersId[0] : null;

        if (invoice) {
            viewerDocumentsList.push({
                label: invoiceLabel,
                fileName: `${invoiceLabel}_${invoiceNumber}`,
                report: {
                    parameters: [
                        { name: 'idCe', value: '' + this.idCE },
                        { name: 'idFacture', value: '' + invoiceId },
                        {
                            name: 'subventionVisible',
                            value: this.hasSubAmountColumnParameter(activityType as RouteActivityType)
                        },
                    ],
                    code: this.getCodeReportsByActivityType(activityType)
                }
            });
        }

        if (deliveryVouchers) {
            const voucherNumber = deliveryVouchersId.join('_');
            viewerDocumentsList.push({
                label: 'Bon(s) de livraison',
                fileName: 'Bon_livraison_' + voucherNumber,
                report: {
                    parameters: [
                        { name: 'listeIdBlBRString', value: '' + deliveryVouchersId.join(',') },
                    ],
                    code: CodesReports.BonLivraisonBonRetour,
                },
            });
        }

        if (saleReceipts) {
            viewerDocumentsList.push({
                label: 'Reçu de vente',
                fileName: 'reçu_vente_commande_' + orderId,
                report: {
                    parameters: [
                        { name: 'idDelegation', value: '' + this.idCE },
                        { name: 'idCde', value: '' + orderId },
                    ],
                    code: CodesReports.RecuVente,
                }
            });
        }

        if (orders) {
            viewerDocumentsList.push({
                label: 'Commande',
                fileName: 'Commande_' + orderId,
                report: {
                    parameters: [
                        { name: 'idCe', value: '' + this.idCE },
                        { name: 'idCommande', value: '' + orderId }
                    ],
                    code: CodesReports.CommandeVente,
                }
            });
        }

        if (receipt) {
            viewerDocumentsList.push({
                label: 'Ticket de caisse',
                fileName: 'Ticket_De_Caisse_' + invoiceId,
                report: {
                    parameters: [
                        { name: 'idDelegation', value: '' + this.idCE },
                        { name: 'idFacture', value: '' + invoiceId }
                    ],
                    code:  CodesReports.TICKET_CAISSE,
                }
            });
        }

        if (forms) {
            viewerDocumentsList.push({
                label: 'Formulaire',
                fileName: 'Formulaire',
                report: {
                    parameters: [{
                        name: 'IdImpression',
                        value: '' + printInformations.printId
                    }],
                    code: CodesReports.IMPRESSION_FORMULAIRE,
                }
            });
        }

        return viewerDocumentsList;
    }

    getSupplierViewerDocumentsListByPrintInfos(printInformations: ActivityPrintMailInformations, printSelection: ActivityPrintMailSelection): ViewerDocument[] {
        const {
            invoiceId,
            ordersId,
            invoiceLabel,
            invoiceNumber,
            deliveryVouchersId,
            chequeLetterPrintId,
            budgetCommitmentPrintId,
        } = printInformations;
        const viewerDocumentsList: ViewerDocument[] = [];
        const { invoice, orders, chequeLetter, budgetCommitment, deliveryVouchers } = printSelection;

        if (invoice) {
            viewerDocumentsList.push({
                fileName: `${invoiceLabel}_${invoiceNumber}`,
                report: {
                    parameters: [
                        { name: 'idCe', value: '' + this.idCE },
                        { name: 'idFacture', value: '' + invoiceId },
                        {
                            name: 'subventionVisible',
                            value: this.hasSubAmountColumnParameter()
                        },
                    ],
                    code: CodesReports.FactureFournisseur
                }
            });
        }

        if (orders) {
            viewerDocumentsList.push({
                label: 'Commande',
                fileName: 'Commande_fournisseur_' + ordersId,
                report: {
                    parameters: [
                        { name: 'idCe', value: '' + this.idCE },
                        { name: 'idCommande', value: '' + ordersId }
                    ],
                    code: CodesReports.CommandeFournisseur,
                }
            });
        }

        if (chequeLetter) {
            viewerDocumentsList.push({
                label: 'Lettre chèque',
                fileName: 'Lettre_cheque',
                report: {
                    code: CodesReports.LettreCheque,
                    parameters: [{
                        name: 'idImpression',
                        value: '' + chequeLetterPrintId
                    }]
                },
            });
        }

        if (budgetCommitment) {
            viewerDocumentsList.push({
                label: 'Engagement budgétaire',
                fileName: 'Engagement_budgetaire',
                report: {
                    code: CodesReports.ENG_BUDG_ECRITURE,
                    parameters: [{
                        name: 'idImpression',
                        value: '' + budgetCommitmentPrintId
                    }]
                },
            });
        }

        if (deliveryVouchers) {
            const voucherNumber = deliveryVouchersId.join('_');
            viewerDocumentsList.push({
                label: 'Bon(s) de livraison',
                fileName: 'Bon_livraison_' + voucherNumber,
                report: {
                    parameters: [
                        { name: 'listeIdBlBRString', value: '' + deliveryVouchersId.join(',') },
                    ],
                    code: CodesReports.BonLivraisonBonRetour,
                },
            });
        }

        return viewerDocumentsList;
    }

    setApplicationParameter(parameterCode: string, value: boolean, activityId?: number): ParametresUserAppliModelDto {
        activityId = activityId || this.store.selectSnapshot(ActivitySalesState.activityId);
        const parameters = cloneDeep(this.store.selectSnapshot(UserState.applicationParameters));

        const filteredParameters = [
            ...parameters.filter(p => p.IdActivite === activityId), // paramètres de l'activité
            ...parameters.filter(p => p.IdActivite === null) // paramètres par défaut
        ];

        const param = filteredParameters.find(p => p.CodeChamp === parameterCode);
        if (!!param) {
            param.CodeValeur = value ? '1' : '0';
        }
        return param;
    }

    /**
     * Update application parameters
     */
    saveApplicationParameters(updatedParametersList: ParametresUserAppliModelDto[]): Observable<any> {
        return this.store.dispatch(new UserUpdateApplicationParametersAction(updatedParametersList));
    }

    /**
     * Génère le pdf du formulaire correspondant à l'impression afin de le joindre au mail
     */
    getFormVoucher$(printId: number): Observable<FichierCompletModel> {
        if (!printId) {
            return throwError(() => new Error('Une erreur s\'est produite lors de la génération du document de formulaire'));
        }

        return this.reportPrintService.getPdfContent$([{
            code: CodesReports.IMPRESSION_FORMULAIRE,
            parameters: [{name: 'IdImpression', value: '' + printId}]
        }]).pipe(
            map(([fileBytes]) => {
                if (!fileBytes) {
                    throw new Error('Une erreur s\'est produite lors de la génération du document de formulaire');
                }
                return fileBytes;
            }),
            map(fileBytes => ({
                IdLienFichier: 0,
                IsCommentable: false,
                IsLikeable: false,
                IdFile: null,
                Taille: 0,
                Extension: 'pdf',
                FileBytes: fileBytes,
                Filename: 'Formulaire',
                LibelleLien: 'Formulaire',
                StatutCode: 'VAL',
                DateDebut: null,
                DateFin: null,
                EditCategCode: null,
                TypeFichierIcone: null,
                TypeFichierLibelle: null,
                RemarqueLien: null,
                Url: null
            }))
        );
    }

    /**
     * Enregistre le modèle d'impression du formulaire des commandes et retourne l'identifiant de l'impression
     */
    generateFormPrintId$(ordersId: number[], delegationId: number = this.idCE): Observable<number> {
        return this.getFormCourrierId$().pipe(
            switchMap(courrierId =>
                this.getFormPrintParameters$(courrierId).pipe(
                    switchMap(parameters => {
                        if (!parameters || parameters.length === 0) {
                            return throwError(() => new Error('Parameters not found'));
                        }

                        const updatedParameters = parameters.map(param => ({
                            ...param,
                            ValeurParametre: param.CodeParametre === 'FORM_ID_CE'
                                ? `${delegationId}`
                                : param.CodeParametre === 'FORM_IDS_CDE'
                                    ? (ordersId ?? []).join(',')
                                    : param.ValeurParametre
                        }));

                        return this.saveFormPrintModel$(courrierId, updatedParameters);
                    })
                )
            )
        );
    }

    /**
     * Document de formulaire proposé à la sélection dans la modale de mail, généré uniquement lors de l'envoi
     */
    getFormMailDocument(generatePrintId$: () => Observable<number>): GeneratedMailDocument {
        return {
            label: 'Formulaire',
            selected: true,
            generate$: () => generatePrintId$().pipe(
                switchMap(printId => this.getFormVoucher$(printId))
            )
        };
    }

    getMailParameters(mailInformations: ActivityPrintMailInformations,
                      mailSelection: ActivityPrintMailSelection,
                      generateFormPrintId$: (() => Observable<number>) | null = null): MailParameters[] {
        const mail: MailParameters[] = [];

        if (mailSelection.orders) {
            mail.push({
                publipostageCode: 'DEVIS',
                idsList: mailInformations.ordersId ?? [],
                label: 'Commande',
                documentCodesList: mailSelection.isContextBenefit ? [CodesReports.DRP] : [CodesReports.CommandeVente],
            });
        }

        if (mailSelection.invoice) {
            mail.push({
                publipostageCode: 'FAC',
                idsList: mailInformations.invoiceId == null ? [] : [mailInformations.invoiceId],
                label: 'Facture',
                documentCodesList: [CodesReports.FactureVente],
            });
        }

        if (mailSelection.forms) {
            mail.push({
                publipostageCode: 'DEVIS',
                idsList: mailInformations.ordersId ?? [],
                label: 'Formulaire',
                generatedDocumentsList: generateFormPrintId$ ? [this.getFormMailDocument(generateFormPrintId$)] : []
            });
        }

        if (mail.length === 0 && mailSelection.isWithoutSelection) {
            mail.push({
                publipostageCode: 'DEVIS',
                idsList: mailInformations.ordersId ?? [],
                label: 'Commande',
                documentCodesListMode: 'skip',
                documentCodesList: [] // doesn't filters because an array empty means it is open bar...
            });
        }

        return mail;
    }

    /**
     * Mise à jour des information pour l'impression d'un nouvel achat
     */
    savePurchasesPrintParameters(values: ActivityPurchasesNewOrderSelectPrintsDialogData, activityId): Observable<ActivityPurchasesNewOrderSelectPrintsDialogData> {
        const appParams = this.applicationParameters.filter(p => p.IdActivite === activityId);

        const getParam = ({ code, value }: { code: string, value: boolean }) => ({
            ...appParams.find(({ CodeChamp }) => CodeChamp === code),
            CodeValeur: !!value ? '1' : '0'
        });

        return this.store.dispatch(new UserUpdateApplicationParametersAction([
            getParam({ code: applicationCodes.IMPCOF, value: values.printOrder }),
            getParam({ code: applicationCodes.IMPPIC, value: false })
        ])).pipe(map(() => values));
    }

    getAppParameterFromCode(code: string, activityId: number = null): string {
        return this.store.selectSnapshot(UserState.applicationParameters).find(p => (p.CodeChamp === code && p.IdActivite === activityId))?.CodeValeur || '0';
    }

    getViewerDocumentsDialogConfigByOrderId(orderId: number): Observable<KalDialogConfig<ViewerDocumentDialogData>> {
        return this.graphqlService.mutateMultiple<'GetIdCeCommande', MutationGetIdCeCommandeArgs>({
            mutation: gql`
                mutation GetIdCeCommande($contexte: ContextInfosInput, $idCommande: Float) {
                    GetIdCeCommande(contexte: $contexte, idCommande: $idCommande)
                }
            `,
            variables: {
                idCommande: orderId
            }
        }).pipe(
            map(({ GetIdCeCommande }) => {
                return new KalDialogConfig<ViewerDocumentDialogData>({
                    title: 'Imprimer la commande',
                    disableClose: true,
                    backdropClass: 'cdk-overlay-dark-backdrop',
                    data: {
                        viewerDocumentsList: [{
                            report: {
                                code: CodesReports.CommandeVente,
                                parameters: [
                                    { name: 'idCe', value: GetIdCeCommande + '' },
                                    { name: 'idCommande', value: orderId + '' }
                                ]
                            },
                            fileName: 'Vente_' + orderId,
                        }]
                    }
                });
            }),
        );
    }

    getHasReportsChequeLetter(type: 'supplier' | 'beneficiary'): Observable<boolean> {
        return this.graphqlService.mutateOne<'HaveReportsLettreCheque', MutationHaveReportsLettreChequeArgs>({
            mutation: gql`
                mutation HaveReportsLettreCheque($contexte: ContextInfosInput, $typeDestinataire: String) {
                    HaveReportsLettreCheque(contexte: $contexte, typeDestinataire: $typeDestinataire)
                }
            `,
            variables: { typeDestinataire: type === 'supplier' ? 'F' : 'C' }
        });
    }

    getPurchasesPrintDialogData({ isGeneric, hasChequeLetter }: { isGeneric: boolean, hasChequeLetter: boolean }): ActivityPurchasesNewOrderSelectPrintsDialogData {
        const appParams = this.applicationParameters;
        const hasChequeDeposit = arrayNotEmpty(this.store.selectSnapshot(ActivityPurchasesNewOrderState.checkIdsList));

        return {
            printOrder: appParams.find(param => param.CodeChamp === 'IMPCOF')?.CodeValeur === '1' || false,
            printChequeLetter: false,
            ANCVExport: false,
            showChequeLetter: hasChequeLetter && hasChequeDeposit,
            showANCVExport: isGeneric,
            doNotShowAgain: false,
        };
    }

    private getFormCourrierId$(): Observable<number> {
        return this.graphqlService.mutateOne<'GetListeCourrierGenerique', MutationGetListeCourrierGeneriqueArgs>({
            mutation: gql`
                mutation GetListeCourrierGenerique($contexte: ContextInfosInput) {
                    GetListeCourrierGenerique(
                        contexte: $contexte
                        criteres: {
                            CodeTypeReport: "FRM"
                        }
                    ){
                        CourrierDTO  {
                            Id
                        }
                    }
                }
            `
        }).pipe(
            map(GetListCourrierGenerique => GetListCourrierGenerique?.CourrierDTO?.[0]?.Id || null)
        );
    }

    private getFormPrintParameters$(courrierId: number): Observable<ParametreImpressionDto[]> {
        return this.graphqlService.mutateOne<'InitialisationModeleImpression', MutationInitialisationModeleImpressionArgs>({
            mutation: gql`
                mutation InitialisationModeleImpression($contexte: ContextInfosInput, $Criteres: CriteresCourrierDTOInput, $CodeReport: String) {
                    InitialisationModeleImpression(
                        contexte: $contexte
                        Criteres: $Criteres
                        CodeReport: $CodeReport
                    ) {
                        ListeParametresImpression {
                            ParametreImpressionDTO {
                                IdParametre
                                CodeParametre
                            }
                        }
                    }
                }
            `, variables: {
                Criteres: {
                    Id: courrierId,
                    CodeReport: CodesReports.IMPRESSION_FORMULAIRE,
                    IsClotured: false
                },
                CodeReport: CodesReports.IMPRESSION_FORMULAIRE
            }
        }).pipe(
            map(InitialisationModeleImpression => InitialisationModeleImpression?.ListeParametresImpression?.ParametreImpressionDTO)
        );
    }

    private saveFormPrintModel$(courrierId: number, parameters: ParametreImpressionDto[]): Observable<number> {
        return this.graphqlService.mutateOne<'SaveModeleImpression', MutationSaveModeleImpressionArgs>({
            mutation: gql`
                mutation SavePrint(
                    $contexte: ContextInfosInput,
                    $SaveModel: SaveModelImpressionDTOInput
                ){
                    SaveModeleImpression(
                        contexte: $contexte
                        SaveModel: $SaveModel
                    )
                }
            `,
            variables: {
                SaveModel: {
                    CodeReport: CodesReports.IMPRESSION_FORMULAIRE,
                    Courrier: {
                        Id: courrierId
                    },
                    ListeParametresImpression: {
                        ParametreImpressionDTO: parameters
                    }
                }
            }
        });
    }

    private getCodeReportsByActivityType(activityType: string): string {
        return activityType === RouteActivityType.Rentals ? CodesReports.FactureLocation : CodesReports.FactureVente;
    }

    /**
     * Récupère la valeur du paramètre d'application en fonction du code
     */
    private getApplicationParameterFromCode(parameterCode: string): ParametresUserAppliModelDto {
        const predicate = ({ CodeChamp }) => CodeChamp === parameterCode;
        return this.getApplicationParametersFilteredActivityId.find(predicate) || this.defaultApplicationParameters.find(predicate) || this.applicationParameters.find(predicate);
    }

    /**
     * Déduit si oui ou non on affiche la colonne "Mnt. Sub. Unit." dans une facture. (param appli "FVUSUB")
     * @param activityTypeCode Code type activité (optionnel)
     * @return Valeur pour l'API d'impression 'True' ou 'False'
     */
    private hasSubAmountColumnParameter(activityTypeCode?: RouteActivityType): string {
        const currentActivityId = this.store.selectSnapshot(ActivitySalesState.currentActivityId);
        const predicate = (param: ParametresUserAppliModelDto): boolean => param.CodeChamp === applicationCodes.FVUSUB && param.IdActivite === currentActivityId && param.CodeValeur === '1';
        const hasParam = this.applicationParameters.some(predicate);

        let paramValue = '1'; // Reprise de la description G+ : Initialisation du paramètre à 1 car c'est la valeur dans CHP_VALDEF

        if (hasParam) {
            paramValue = this.applicationParameters.find(predicate)?.CodeValeur;
        }

        return (!activityTypeCode || activityTypeCode !== RouteActivityType.Benefits) && paramValue ? 'True' : 'False';
    }
}
